import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useZone, Zone } from '../contexts/ZoneContext';
import { hasSubscription, canAccessSymbol, canAccessBoringBucket } from '../lib/entitlements';
import { ENTITLEMENTS } from '../constants/entitlements';

// ─── Nav items per zone ─────────────────────────────────────────────────────

interface NavItem { path: string; label: string; beta?: boolean; }
interface NavSection { title: string; items: NavItem[]; }

const WEALTH_SECTIONS: NavSection[] = [
  { title: 'Overview', items: [
    { path: '/dashboard', label: 'Dashboard' },
  ]},
  { title: 'Plan', items: [
    { path: '/bucket-visualizer', label: 'Bucket Visualizer' },
    { path: '/fire-calculator', label: 'FIRE Strategy' },
    { path: '/lifecycle-investment', label: 'Lifecycle Planner' },
  ]},
  { title: 'Track', items: [
    { path: '/net-worth', label: 'Net Worth' },
  ]},
  { title: 'Learn', items: [
    { path: '/playbook', label: 'Playbook' },
    { path: '/learn/5-pillars', label: '5 Pillars' },
    { path: '/learn/4-bucket-system', label: '4-Bucket Guide' },
  ]},
];

function getTradeSections(
  showV1?: boolean,
  showAlgo?: boolean,
  showOptionsAutoTrader?: boolean,
  showLearnToTrade?: boolean,
  showSpx?: boolean,
  showSpy?: boolean,
  showFutures?: boolean,
  showNotches?: boolean,
  showNotchesDashboard?: boolean,
  showBoringBucket?: boolean,
  showTtStockPaper?: boolean,
  showAdminAudit?: boolean,
): NavSection[] {
  // V1 + V2 nav surfaces hidden 2026-05-25 -- Notches is the primary path
  // for SPX/SPY/ES/MES content. Pages remain reachable via direct URL for
  // legacy bookmarks. Multi-phase retirement plan:
  // docs/wiki/decisions/2026-05-25-v1-v2-cleanup-plan.md
  const spxItems: NavItem[] = [];
  const indexItems: NavItem[] = [];
  const futuresItems: NavItem[] = [];

  // Notches sidebar - top of the menu because Options + Stocks (equity, Premier
  // baseline) live here for every signed-in user. Symbol-specific items
  // (Dashboard / Markets / Audit) are still gated by entitlements.
  const notchesItems: NavItem[] = [];
  if (showNotchesDashboard) notchesItems.push({ path: '/notches/dashboard', label: 'Dashboard' });
  notchesItems.push({ path: '/options-research', label: 'Options' });
  notchesItems.push({ path: '/stock-research', label: 'Stocks' });
  notchesItems.push({ path: '/notches/events', label: 'Events' });
  if (showNotches) {
    notchesItems.push({ path: '/notches', label: 'Markets' });
  }
  // Audit pillar -- admin-only. The API gates by allowlist; the nav hides it
  // for non-admins so subscribers don't see a link that 403s. Until we build
  // a redacted subscriber view, scoring internals (sigmas, penalty values,
  // AI prompt tokens) shown in the audit stay internal.
  if (showAdminAudit) {
    notchesItems.push({ path: '/notches/audit', label: 'Audit' });
  }

  const reportsItems: NavItem[] = [];
  if (showNotches || showBoringBucket) {
    reportsItems.push({ path: '/notches/reports', label: 'Weekly Reports' });
  }

  const sections: NavSection[] = [
    { title: 'Notches', items: notchesItems },
    { title: 'Reports', items: reportsItems },
  ];

  // The Boring Bucket — mentored membership. Members only; non-members get the
  // CTA banner instead.
  if (showBoringBucket) {
    sections.push({ title: 'The Boring Bucket', items: [
      { path: '/boring-bucket', label: 'Overview' },
      { path: '/boring-bucket/founder-trades', label: "Founder's Trades" },
      { path: '/boring-bucket/mentoring', label: 'Mentoring' },
      { path: '/boring-bucket/capital-plan', label: 'Capital Plan' },
    ]});
  }

  // SPX/SPY/ES-MES sections only show if the user has the matching product flag.
  // Component-level paywalls still gate page content for direct-URL access — this
  // hides the menu entries so non-subscribers don't see what they don't have.
  if (showSpx     && spxItems.length     > 0) sections.push({ title: 'SPX',              items: spxItems     });
  if (showSpy     && indexItems.length   > 0) sections.push({ title: 'Index (SPY)',      items: indexItems   });
  if (showFutures && futuresItems.length > 0) sections.push({ title: 'Futures (ES/MES)', items: futuresItems });

  if (showAlgo) {
    sections.push({ title: 'Alpaca Stock Trader', items: [
      { path: '/algo/engines', label: 'Engines' },
      { path: '/algo/signals', label: 'Signals' },
      { path: '/algo/trades', label: 'Trades' },
      { path: '/algo/portfolio', label: 'Portfolio' },
      { path: '/algo/audit', label: 'Audit' },
    ]});
  }

  if (showOptionsAutoTrader) {
    sections.push({ title: 'TT Options Trader', items: [
      { path: '/options-trader/engines', label: 'Engines' },
      { path: '/options-trader/trades', label: 'Trades' },
      { path: '/options-trader/analytics', label: 'Analytics' },
      { path: '/options-trader/audit', label: 'Audit Log' },
    ]});
    sections.push({ title: 'TT Options Trader v2 (paper)', items: [
      { path: '/options-trader-v2/engines', label: 'Engines' },
      { path: '/options-trader-v2/trades', label: 'Trades' },
      { path: '/options-trader-v2/analytics', label: 'Analytics' },
      { path: '/options-trader-v2/audit', label: 'Audit Log' },
    ]});
  }

  if (showTtStockPaper) {
    sections.push({ title: 'TT Stocks', items: [
      { path: '/tt-stock/engines', label: 'Engines' },
      { path: '/tt-stock/trades', label: 'Trades' },
      { path: '/tt-stock/dashboard', label: 'Dashboard' },
      { path: '/tt-stock/audit', label: 'Audit' },
    ]});
  }

  if (showLearnToTrade) {
    sections.push({ title: 'Learn to Trade', items: [
      { path: '/trade-with-me', label: 'Trade Examples' },
    ]});
  }

  return sections;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const Navigation: React.FC = () => {
  const { logout, user } = useAuth();
  const { zone, setZone, zoneColor } = useZone();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const u = user as any;
  const showSpx = canAccessSymbol(user as any, 'SPX');
  const showSpy = canAccessSymbol(user as any, 'SPY');
  const showFutures = canAccessSymbol(user as any, 'ES') || canAccessSymbol(user as any, 'MES');
  // Notches menu visible only if user has notchesEnabled AND at least one symbol subscription.
  // Tabs inside the page are filtered by per-symbol flags (NotchesPage handles that).
  const hasAnySymbolSubscription = showSpx || showSpy || showFutures;
  const showNotches = !!u?.notchesEnabled && hasAnySymbolSubscription;
  // Preview entitlement gives access to the Notches dashboard surface (and
  // its placeholder pillars) even without notchesEnabled - separate gate.
  const showNotchesDashboard = hasSubscription(user, ENTITLEMENTS.NOTCHES_PREVIEW);
  const showBoringBucket = canAccessBoringBucket(user as any);
  // TT Stock Paper engine is a single-user Bobby-only paper trading dashboard
  // backed by /admin/tt-stock-report (which itself enforces email allowlist).
  const showTtStockPaper = user?.email === 'bobby@bv-holdings.com'
    || user?.email === 'bobby_malik@hotmail.com';
  // Audit page exposes internal scoring (sigmas, penalty values, AI prompt
  // tokens) -- admin allowlist only. API enforces the same allowlist.
  const showAdminAudit = user?.email === 'bobby_malik@hotmail.com'
    || user?.email === 'bobby@bv-holdings.com';
  const sections = zone === 'wealth' ? WEALTH_SECTIONS : getTradeSections(
    user?.showV1Signals,
    user?.algoTradingEnabled,
    user?.optionsAutoTradingEnabled,
    u?.tradeWithMeEnabled,
    showSpx,
    showSpy,
    showFutures,
    showNotches,
    showNotchesDashboard,
    showBoringBucket,
    showTtStockPaper,
    showAdminAudit,
  );

  useEffect(() => { setMobileOpen(false); setProfileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleZoneSwitch = (z: Zone) => {
    setZone(z);
    // Always navigate to the zone's home page when switching
    if (z === 'wealth') navigate('/dashboard');
    else if (z === 'trade') navigate('/options-research');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* ── Top Bar ── */}
      <div className="ztop">
        <div className="ztop-left">
          <button className="ztop-hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
            <span /><span /><span />
          </button>
          <Link to="/dashboard" className="ztop-brand">
            <div className="ztop-logo" style={{ background: zoneColor }}>W</div>
            <span className="ztop-brand-text">Wealth Financial Systems</span>
          </Link>
          <div className="ztop-zones">
            <button
              className={`ztop-zone ${zone === 'wealth' ? 'ztop-zone-active' : ''}`}
              style={zone === 'wealth' ? { borderBottomColor: '#10b981', color: '#10b981' } : {}}
              onClick={() => handleZoneSwitch('wealth')}
            >Wealth AI</button>
            {/* Notches AI is the default landing surface (2026-05-25).
                Trade AI zone button retired -- /trade-signals remains
                accessible via the sidebar. */}
            <button
              className={`ztop-zone ${location.pathname.startsWith('/notches') ? 'ztop-zone-active' : ''}`}
              style={location.pathname.startsWith('/notches')
                ? { borderBottomColor: '#8b5cf6', color: '#8b5cf6' }
                : { color: '#8b5cf6' }}
              onClick={() => { setZone('trade'); navigate('/notches/dashboard'); }}
              title="Notches AI"
            >
              Notches AI
            </button>
            {/* Cross-link to the public /n2 site (briefings + scorecard +
                research + methodology). "Public site" is a utility label
                that names what happens on click without colliding with
                any product name -- 'Notches AI' next to it is the paid
                dashboard, 'Notches by WFS' would collide with the /n2
                brand mark, 'Research' collides with the Notches Research
                product on the app side. */}
            <button
              className={`ztop-zone ${location.pathname.startsWith('/n2') ? 'ztop-zone-active' : ''}`}
              style={location.pathname.startsWith('/n2')
                ? { borderBottomColor: '#bf953f', color: '#bf953f' }
                : { color: '#bf953f' }}
              onClick={() => navigate('/n2')}
              title="Public site (/n2)"
            >
              Public site
            </button>
          </div>
        </div>

        <div className="ztop-right">
          <div className="ztop-profile" ref={profileRef}>
            <button className="ztop-profile-btn" onClick={() => setProfileOpen(!profileOpen)}>
              <span className="ztop-email">{user?.email?.split('@')[0] || 'User'}</span>
              {user?.effectiveTier && <span className="ztop-tier">{user.effectiveTier}</span>}
              <span className="ztop-caret">{profileOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {profileOpen && (
              <div className="ztop-dropdown">
                <Link to="/profile" className="ztop-dropdown-item">Account Settings</Link>
                <Link to="/upgrade" className="ztop-dropdown-item">Subscription</Link>
                <div className="ztop-dropdown-divider" />
                <Link to="/help" className="ztop-dropdown-item">Help Docs</Link>
                <Link to="/contact-support" className="ztop-dropdown-item">Contact Support</Link>
                <div className="ztop-dropdown-divider" />
                <button className="ztop-dropdown-item ztop-dropdown-logout" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile overlay ── */}
      {mobileOpen && <div className="zsidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* ── Left Sidebar ── */}
      <aside className={`zsidebar ${mobileOpen ? 'zsidebar-open' : ''}`} style={{ borderRightColor: zoneColor + '40' }}>
        <nav className="zsidebar-nav">
          {/* Boring Bucket CTA — top of the Trade AI sidebar, non-members only.
              Members get the section nav instead. */}
          {zone === 'trade' && !showBoringBucket && (
            <a
              href="https://theboringbucket.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', textDecoration: 'none',
                background: 'linear-gradient(135deg, rgba(16,185,129,0.14), rgba(59,130,246,0.14))',
                border: '1px solid rgba(16,185,129,0.35)', borderRadius: 8,
                padding: '12px 14px', margin: '0 0 14px',
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', letterSpacing: 0.4 }}>GET MENTORED</div>
              <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginTop: 3 }}>The Boring Bucket</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, lineHeight: 1.5 }}>
                Watch a real account live and learn the framework. Apply &rarr;
              </div>
            </a>
          )}
          {sections.filter((section) => section.items.length > 0).map((section) => (
            <div key={section.title} className="zsidebar-section">
              <div className="zsidebar-section-label" style={{ color: zoneColor }}>{section.title}</div>
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`zsidebar-link ${isActive(item.path) ? 'zsidebar-link-active' : ''}`}
                  style={isActive(item.path) ? { color: '#fbbf24', background: '#fbbf2410', fontWeight: 700 } : {}}
                >
                  {item.label}
                  {item.beta && <span className="zsidebar-beta">BETA</span>}
                </Link>
              ))}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};
